








































































































































































































import Component from "vue-class-component";
import Multiselect from "vue-multiselect";
import { RingLoader } from "vue-spinners-css";

import { BaseRootPageComponent } from "./BaseRootPageComponent";
import SelectPatientsRow from "./SelectPatientsRow.vue";
import { PatientListViewModel, Watcher, AuthUser } from "@/models";
import _auth from "@/utils/auth";
import { EventBus, EventNames } from "@/utils/eventBus";
import _gateway from "@/utils/gateway";
import { sortByFullName, sortByInitials } from "@/utils/names";
import WatcherModal from "./modals/WatcherModal.vue";
import { updateGTMGlobalVariables } from "@/utils/gtm";

export const UnmappedCareAreaName = "Other";

export interface CareArea {
  name: string;
  isSelected: boolean;
  count: number;
}

@Component({
  components: { Multiselect, RingLoader, SelectPatientsRow, WatcherModal }
})
export default class SelectPatients extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  isDestroyed = false;

  loading = true;
  patients: PatientListViewModel[] = [];
  careAreas: CareArea[] = [];
  filteredPatients: PatientListViewModel[] = [];
  submitting = false;
  error = "";
  showingCareAreaDropdown = false;
  showModal = false;
  modalWatchers: Watcher[] = [];

  get anyPatientSelected() {
    return this.patients.some(p => p.isSubscribed);
  }

  get areAllFilteredPatientsSelected() {
    return this.filteredPatients.every(p => p.isSubscribed);
  }

  get numSubscribedPatients() {
    return this.patients.filter(p => p.isSubscribed).length;
  }

  get selectedCareAreas() {
    return this.careAreas.filter(c => c.isSelected);
  }

  async created() {
    // updateGTMGlobalVariables(this);

    try {
      await this.validateIdnIdAndClientId();

      this.setTitle("Select Patients");
      await this.loadPatients();

      // Subscribe after loading the patients, so that we don't refresh while loading the patients
      this.subscribeToEvents();
    } finally {
      this.loading = false;
    }
  }

  private async validateIdnIdAndClientId() {
    if (this.auth.clientId && !this.auth.idnId) {
      const idns = await this.gateway.getIdnsAndClients();
      const idn = idns.find(i => i.clients.some(c => c.id === this.auth.clientId));
      if (idn) {
        this.auth.setIdnId(idn.id);
      }
    }
  }

  subscribeToEvents() {
    // If the component has already been destroyed (e.g. the user navigated away while the initial data was being
    // loaded), don't register for the data refresh event otherwise, it will never be removed and will keep running
    // until the user reloads the webpage.
    if (this.isDestroyed) return;

    EventBus.$on(EventNames.DataRefresh, this.loadPatients);
  }

  beforeDestroy() {
    EventBus.$off(EventNames.DataRefresh, this.loadPatients);
    this.isDestroyed = true;
  }

  async loadPatients() {
    // If somehow the component was already destroyed, don't bother loading
    if (this.isDestroyed) return;

    let newPatients = await this.gateway.getAllPatients();
    // Sort the patients alphabetically
    newPatients = newPatients.sort(sortByFullName);
    // Sort the watchers alphabetically (sort by full name so they are sorted for the modal too)
    newPatients.forEach(p => (p.watchers = p.watchers.sort(sortByInitials)));

    // If the patients are being refreshed, copy the user's selection to the new patients
    if (this.patients && this.patients.length > 0) {
      newPatients.forEach(newPatient => {
        const patient = this.patients.find(p => p.id === newPatient.id);

        // If the new patient's selection don't match the user's current selection, update the new patient to match
        if (patient && patient.isSubscribed != newPatient.isSubscribed) {
          newPatient.isSubscribed = patient.isSubscribed;
          this.updatePatientWatchers(newPatient);
        }
      });
    }

    this.patients = newPatients;
    this.initializeCareAreas();
  }

  private initializeCareAreas() {
    let newCareAreas: CareArea[] = [];
    this.patients.forEach(p => {
      const careAreaName = p.location.careArea || UnmappedCareAreaName;
      const careArea = newCareAreas.find(ca => ca.name === careAreaName);

      if (!careArea) {
        newCareAreas.push({ name: careAreaName, count: p.isSubscribed ? 1 : 0, isSelected: false });
      } else if (p.isSubscribed) {
        careArea.count++;
      }
    });

    // Sort the care areas alphabetically, always put "Other" at the end
    newCareAreas = newCareAreas.sort((a, b) => {
      if (a.name === UnmappedCareAreaName) return 1;
      if (b.name == UnmappedCareAreaName) return -1;

      return a.name > b.name ? 1 : -1;
    });

    // If the data is being refreshed, copy the isSelected flag to the new care areas
    if (this.careAreas && this.careAreas.length > 0) {
      newCareAreas.forEach(newCareArea => {
        const c = this.careAreas.find(c => c.name == newCareArea.name);
        if (c) newCareArea.isSelected = c.isSelected;
      });
    }

    this.careAreas = newCareAreas;
    this.updateFilteredPatients();
  }

  togglePatient(patient: PatientListViewModel) {
    if (this.loading) return;

    patient.isSubscribed = !patient.isSubscribed;

    this.updatePatientWatchers(patient);
  }

  toggleSelectAll() {
    const selection = !this.areAllFilteredPatientsSelected;
    this.filteredPatients.forEach(p => (p.isSubscribed = selection));
    this.updateAllPatientsWatchers();
  }

  private updateCareAreaSelectedPatientCount(patient: PatientListViewModel) {
    const careArea = this.careAreas.find(ca => ca.name === (patient.location?.careArea || UnmappedCareAreaName));
    if (careArea) {
      // Don't just add/subtract 1 because the event could be firing multiple times, so recalculate the count each time
      careArea.count = this.patients.filter(
        p => p.isSubscribed && p.location?.careArea === patient.location?.careArea
      ).length;
    }
  }

  private updatePatientWatchers(patient: PatientListViewModel, watcher?: Watcher) {
    if (!watcher) {
      watcher = this.generateWatcher();
    }

    // Update the patient's watcher list to add/remove the user
    const index = patient.watchers.findIndex(w => w.id === watcher?.id);
    if (patient.isSubscribed && index < 0) {
      patient.watchers.push(watcher);

      // Resort the watchers after adding the current user
      patient.watchers = patient.watchers.sort(sortByInitials);
    } else if (!patient.isSubscribed && index >= 0) {
      patient.watchers.splice(index, 1);
    }

    // Update the care area's selected patient count
    this.updateCareAreaSelectedPatientCount(patient);
  }

  private updateAllPatientsWatchers() {
    const watcher: Watcher = this.generateWatcher();

    this.filteredPatients.forEach(patient => {
      this.updatePatientWatchers(patient, watcher);
    });
  }

  careAreaChanged() {
    this.updateFilteredPatients();
  }

  private generateWatcher(): Watcher {
    const user = this.auth.user as AuthUser;
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName
    };
  }

  private updateFilteredPatients() {
    const selectedCareAreaNames = this.selectedCareAreas.map(ca => ca.name) || [];
    if (selectedCareAreaNames.length === 0) {
      this.filteredPatients = this.patients;
    } else {
      this.filteredPatients = this.patients.filter(p =>
        selectedCareAreaNames.includes(p.location.careArea || UnmappedCareAreaName)
      );
    }
  }

  async submit() {
    this.submitting = true;
    this.error = "";
    const patientIds = this.patients.filter(p => p.isSubscribed).map(p => p.id);

    try {
      await this.gateway.subcribeToPatients(patientIds, this.auth.pushInfo);
      this.$router.push("/my-patients");
    } catch (error) {
      this.error = "An unknown error occurred";
    } finally {
      this.submitting = false;
    }
  }

  showWatchersModal(patient: PatientListViewModel) {
    this.modalWatchers = patient.watchers.filter((w: Watcher) => w.id !== this.auth.user?.id);
    this.showModal = this.modalWatchers.length > 0;
  }
}
